import {SummaryCalculations} from "@client/calculations";
import {
    ArcElement,
    Chart,
    ChartOptions,
    DoughnutController,
    Legend,
    Tooltip,
} from "chart.js";
import "chartjs-adapter-date-fns";
import React from "react";

Chart.register([DoughnutController, ArcElement, Tooltip, Legend]);

export const Macros = (props: {children: React.ReactNode}) => (
    <div
        style={{
            width: 400,
            maxWidth: "100%",
            height: 120,
            margin: "20px auto 0 auto",
        }}
    >
        {props.children}
    </div>
);

type Props = SummaryCalculations & {size?: "share"};

export const PieChart = ({protein, carbs, fat, size}: Props) => {
    const canvasElement = React.useRef<HTMLCanvasElement>(null);
    const chart = <canvas ref={canvasElement} />;

    const macros = [protein, carbs, fat].some((macro) => Number.isNaN(macro.percentage))
        ? [{total: "N/A", percentage: 100, label: null}]
        : ([
              {...protein, label: "Protein"},
              {...carbs, label: "Carbs"},
              {...fat, label: "Fat"},
          ] as const);

    const baseOptions = {
        maintainAspectRatio: false,
        animation: {
            animateRotate: false,
        },
        plugins: {
            legend: {
                position: "left",
                labels: {
                    boxWidth: 40,
                    boxHeight: size == "share" ? 40 : 24,
                    padding: size == "share" ? 20 : 12,
                    color: "#222",
                    font: {
                        family: "InterVariable",
                        weight: 300,
                        size: size == "share" ? 28 : 16,
                    },
                },
            },
            tooltip: {
                titleFont: {
                    family: "InterVariable",
                },
                bodyFont: {
                    family: "InterVariable",
                },
                callbacks: {
                    title: (toolTipItem) => {
                        const index = toolTipItem[0].dataIndex;
                        return macros[index].label ?? "N/A";
                    },
                    label: (toolTipItem) => {
                        const index = toolTipItem.dataIndex;
                        const macro = macros[index];
                        const total = macro.total == null ? 0 : parseInt(macro.total);

                        return `${total} g (${macro.percentage}%)`;
                    },
                },
            },
        },
    } satisfies ChartOptions<"doughnut">;

    React.useEffect(() => {
        if (macros.some((macro) => Number.isNaN(macro.percentage))) {
            return;
        }

        const colors = [
            "oklch(0.673 0.182 276.935)", // indigo-400
            "oklch(0.746 0.16 232.661)", // sky-400
            "oklch(0.718 0.202 349.761)", // pink-400
        ];

        const chart = new Chart(canvasElement.current!, {
            type: "doughnut",
            data: {
                datasets: [
                    {
                        data: macros.map((macro) => macro.percentage),
                        backgroundColor: colors,
                        borderWidth: [1, 1, 1],
                        borderColor: colors,
                    },
                ],
                labels: macros.map((macro) =>
                    macro.label == null
                        ? "N/A"
                        : `${macro.label} ${macro.percentage}%     `,
                ),
            },
            options: baseOptions,
        });
        return () => {
            chart.destroy();
        };
    }, [protein, carbs, fat]);

    return chart;
};
